import type { Cliente } from '../db/tipos'

// ▶ Retorno do cliente (spec 2026-10-06, ciclo 2B). Modulo PURO: as telas so
// desenham. Nada e gravado quando vence (caminho A, decisao dela): tudo e
// calculado do cliente + das datas LOCAIS das visitas ('AAAA-MM-DD').
//
// Aviso excluido nao e apagado pela visita -- e IGNORADO quando deixa de valer.
// A data marcada e apagada por registrarVisita quando a visita vem ANTES dela
// (decisao 06/10); a visita no dia marcado ou depois a encerra por aqui.

export type Retorno = { cliente: Cliente; vencimento: string; diasDeAtraso: number }

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export function somarDias(dataISO: string, dias: number): string {
  const [a, m, d] = dataISO.split('-').map(Number)
  return iso(new Date(a, m - 1, d + dias))
}

function diferencaEmDias(de: string, ate: string): number {
  const [a1, m1, d1] = de.split('-').map(Number)
  const [a2, m2, d2] = ate.split('-').map(Number)
  return Math.round((Date.UTC(a2, m2 - 1, d2) - Date.UTC(a1, m1 - 1, d1)) / 86_400_000)
}

export function dataDoDiaDaSemana(hojeISO: string, diaDeHoje: number, dia: number): string {
  return somarDias(hojeISO, (dia - diaDeHoje + 7) % 7)
}

export function ultimaVisitaAntes(datas: readonly string[] | undefined, limite: string): string | null {
  let ultima: string | null = null
  for (const d of datas ?? []) if (d < limite && (ultima === null || d > ultima)) ultima = d
  return ultima
}

export function vencimentoDoRetorno(c: Cliente, ultimaVisita: string | null): string | null {
  const marcado = c.retorno_marcado ?? null
  // Vale so para o PROXIMO retorno: visita no dia marcado (ou depois) a encerra.
  if (marcado && (ultimaVisita === null || ultimaVisita < marcado)) return marcado
  const dias = c.retorno_dias ?? null
  if (!dias) return null
  // Decisao dela 06/10: conta da data MAIS RECENTE entre a ultima visita e o dia
  // em que o periodo foi marcado/mudado -- marcar hoje nunca nasce atrasado.
  const desde = c.retorno_desde ?? null
  const base = ultimaVisita && desde ? (ultimaVisita > desde ? ultimaVisita : desde) : ultimaVisita ?? desde
  return base ? somarDias(base, dias) : null
}

// Hoje: vencidos e atrasados. Outro dia: so o vencimento exato (decisao 06/10).
// A ultima visita e a de ANTES do dia mostrado: o visitado hoje fica no bloco
// com o selo verde e sai amanha.
export function retornosDoDia(
  clientes: Cliente[], datasPorCliente: Map<string, string[]>, data: string, hoje: string,
): Retorno[] {
  const lista: Retorno[] = []
  for (const c of clientes) {
    if (c.apagado_em !== null || c.status !== 'ativo') continue
    const vencimento = vencimentoDoRetorno(c, ultimaVisitaAntes(datasPorCliente.get(c.id), data))
    if (vencimento === null) continue
    if (data === hoje ? vencimento > hoje : vencimento !== data) continue
    lista.push({ cliente: c, vencimento, diasDeAtraso: Math.max(0, diferencaEmDias(vencimento, data)) })
  }
  return lista.sort(
    (a, b) => b.diasDeAtraso - a.diasDeAtraso || a.cliente.razao_social.localeCompare(b.cliente.razao_social),
  )
}

// O que vira "Retorno: <cliente>" na Agenda e (com atraso) na janela vermelha.
export function pendenciasDeRetorno(
  clientes: Cliente[], datasPorCliente: Map<string, string[]>, hoje: string,
): Retorno[] {
  return retornosDoDia(clientes, datasPorCliente, hoje, hoje).filter(
    (r) => !(datasPorCliente.get(r.cliente.id) ?? []).includes(hoje)
      && r.vencimento !== (r.cliente.retorno_aviso_excluido ?? null),
  )
}

export function camposDoRetornoAoSalvar(
  existente: Cliente | undefined, dias: number | null, hoje: string,
): Pick<Cliente, 'retorno_dias' | 'retorno_desde' | 'retorno_marcado' | 'retorno_aviso_excluido'> {
  const antes = existente?.retorno_dias ?? null
  return {
    retorno_dias: dias,
    retorno_desde: dias === null ? null : dias !== antes ? hoje : existente?.retorno_desde ?? hoje,
    retorno_marcado: existente?.retorno_marcado ?? null,
    retorno_aviso_excluido: existente?.retorno_aviso_excluido ?? null,
  }
}

export function lerDiasDeRetorno(texto: string): number | null | 'invalido' {
  const t = texto.trim()
  if (t === '') return null
  if (!/^\d+$/.test(t)) return 'invalido'
  const n = Number(t)
  return n >= 1 && n <= 365 ? n : 'invalido'
}
